import { MolsonBox, MolsonIcon, MolsonTypography } from '@components/reusable'
import type { SxProps, Theme } from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'
import { useQuoteMarginColors } from '../hooks'

export type StateType = 'default' | 'success' | 'error'

type TargetProposedPriceProps = {
  title: string
  price?: string | number
  marginPercentage?: string
  type?: StateType
  fixedHeight?: boolean
  sx?: SxProps<Theme>
}

export const TargetAndProposedPrice = ({
  title,
  price,
  type = 'default',
  marginPercentage,
  fixedHeight = false,
  sx
}: TargetProposedPriceProps) => {
  const theme = molsonUseTheme()

  const defaultStyles = {
    height: fixedHeight ? '135px' : 'unset',
    background: theme.palette.secondary[50],
    padding: '24px',
    borderRadius: 4,
    width: '50%',
    ...sx
  }

  const iconSx = {
    alignItems: 'center',
    borderRadius: 1,
    display: 'flex',
    fontSize: 12,
    fontWeight: 600,
    height: '25px',
    px: 0.75,
    py: 0.25
  }

  const { textColor, borderColor, bgColor } = useQuoteMarginColors(type)

  const iconStates = {
    default: (
      <MolsonIcon variant="CHECK" iconColor="success" fontSize="small" />
    ),
    success: (
      <MolsonIcon variant="CHECK" iconColor="success" fontSize="small" />
    ),
    error: (
      <MolsonIcon
        variant="ARROW_DROP_DOWN"
        iconColor="error"
        fontSize="small"
      />
    )
  }

  const iconType = iconStates[type]

  return (
    <MolsonBox
      display="flex"
      justifyContent="center"
      flexDirection="column"
      boxShadowType="default"
      sx={defaultStyles}
      gap={1}
    >
      <MolsonTypography variant="h4">{title}</MolsonTypography>
      <MolsonBox
        width="100%"
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={1}
      >
        <MolsonTypography variant="h2">{price}</MolsonTypography>
        {marginPercentage && (
          <MolsonTypography
            bgcolor={bgColor}
            border={`1px solid ${borderColor}`}
            color={textColor}
            gap={0.25}
            {...iconSx}
          >
            {iconType ?? null}
            {marginPercentage}%
          </MolsonTypography>
        )}
      </MolsonBox>
    </MolsonBox>
  )
}
